import {
    getBaseSection,
    getCategorySlug,
    getLineupSlug,
    getSubjectsSet,
    getTags,
} from "../../utils/path/library";
import { transformAllContentItems } from "../../utils/dataTransform/library";
import { formatHeadlines } from "../../utils/dataTransform/utils";
import { insertAd, validateApiData } from "./utils";
import { receive_content } from "../../reducers/Content/reducer";
import { set_loading_complete } from "../../actions/Loader/dispatchers";
import { start_loading } from "../../reducers/Loader/reducer";
import { fetchSingleLineup } from "../../fetch/graphql/requests/lineups";
import { fetchContentPackage } from "../../fetch/graphql/contentItem";

import * as CONSTANTS from "../../actions/Content/constants";
import { DEFAULT_PAGE_SIZE, AD_INDEX_LOAD_MORE } from "../../constants/content";
import { SECTIONS } from "../../constants/sections";
import { ERROR_TYPES } from "../../actions/Errors/constants";
import { HttpError } from "../../utils/errors/http";
import log from "../../utils/logger/library";
import { getFeatureFlagString } from "../../utils/flags/library";
import logger from "../../utils/logger/library";

const featureFlagLineup = getFeatureFlagString("lineup");

/**
 * Fetches AllContentItems data for a landing page and dispatches data into the Content store slice.
 * @param {object} options
 * @param {string} options.section Section of ocelot that determines categorySlug, lineupSlug, and subjectSet used to fetch data.
 * @param {boolean} [options.appendContent=false] If true, newly fetched content will be appended to existing state.content.list value. Defaults to false.
 * @param {boolean} [options.maybeInsertAd=false] If true, inserts ad in content. Defaults to false.
 * @param {number} [options.page=1] Number of page for data to be fetched for. Defaults to 1.
 * @param {number} [options.pageSize=DEFAULT_PAGE_SIZE] Amount of content fetched per page. Defaults to DEFAULT_PAGE_SIZE.
 * @param {boolean} [options.isLoadMore] specifies if it is a load more content. Defaults to false.
 * @param {boolean} [options.abTestSlug] specifies the abTestSlug. Defaults to null.
 * @returns {Function} a thunk function
 */
export function fetch_content_graphql({
    section,
    appendContent = false,
    maybeInsertAd = false,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    isLoadMore = false,
    abTestSlug = null,
}) {
    return async (dispatch, getState) => {
        if (!section) {
            return;
        }
        let lineupSlug;
        let subjectsSet;
        let categorySlug;
        let tags;
        let shouldOverrideCategory;
        const currentContentList = getState().content.list;

        try {
            lineupSlug =
                abTestSlug ||
                featureFlagLineup(getState()) ||
                getLineupSlug(section);
            subjectsSet = getSubjectsSet(section);

            shouldOverrideCategory = Object.values(
                CONSTANTS.OVERRIDE_CATEGORY
            ).find((o) => {
                return section.startsWith(o);
            });

            categorySlug = shouldOverrideCategory
                ? "empty-category"
                : getCategorySlug(section);

            const baseSection = getBaseSection(section);
            tags = getTags(section);
            const customizedSection =
                SECTIONS[section] || SECTIONS[baseSection];

            if (!validateApiData({ lineupSlug, subjectsSet, tags })) {
                throw new HttpError(404, "lineup does not exist");
            }

            if (customizedSection) {
                pageSize = customizedSection.customPageSize || pageSize;
            }

            dispatch(start_loading());

            const timestamp = Date.now();
            const { allContentItems } = await fetchSingleLineup({
                lineupSlug,
                categorySlug,
                subjectsSet,
                tags,
                page,
                pageSize,
            });

            formatHeadlines(section, allContentItems.nodes);

            // Do we need to stick an ad in?
            if (maybeInsertAd) {
                //Insert the ad at the configured index in the content list.
                allContentItems.nodes = insertAd(
                    allContentItems.nodes,
                    AD_INDEX_LOAD_MORE
                );
            }

            let contentList = [];

            if (appendContent) {
                contentList = transformAllContentItems(allContentItems.nodes);
                if (contentList?.length >= 1) {
                    contentList[0].focus = true;
                }
                contentList = currentContentList.concat(contentList);
            } else {
                contentList = transformAllContentItems(allContentItems.nodes);
            }

            let receivedContent = {
                section,
                contentList,
                timestamp,
                pageValue: page,
                status: CONSTANTS.STATUS_CODES.SUCCESS,
                hasNextPage: allContentItems.nodes.length === pageSize,
            };

            if (!isLoadMore) {
                const firstItem = contentList[0];
                const contentPackage = firstItem?.packageComponents
                    ? firstItem
                    : null;
                receivedContent = { ...receivedContent, contentPackage };
            }

            dispatch(receive_content({ ...receivedContent, isLoadMore }));
        } catch (error) {
            log.error(
                `(fetch_page_content_graphql) error for ${section}`,
                error
            );

            if (page === 1) {
                // Let the initData handle the error and set the state accordingly
                throw error;
            } else {
                // receive empty content, add error notification and hide the load more button
                dispatch(
                    receive_content({
                        contentList: currentContentList,
                        pageValue: page - 1,
                        section: section,
                        timestamp: Date.now(),
                        hasNextPage: false,
                        status: CONSTANTS.STATUS_CODES
                            .CONTENT_TYPE_NOT_AVAILABLE,
                    })
                );
                log.error(
                    `(fetch_content_graphql) Error: {code: ${ERROR_TYPES.INVALID_CONTENT_REQUEST}, description: Content does not exist for ${section}}`
                );
            }
        } finally {
            if (validateApiData({ lineupSlug, subjectsSet, tags })) {
                dispatch(set_loading_complete());
            }
        }
    };
}

/**
 * Fetches content for a load more section
 *
 * @param {string} section - Section of ocelot that determines categorySlug, lineupSlug, and subjectSet used to fetch data
 * @return {Function} fetch_content_graphql
 */
export function fetch_content_load_more(section) {
    const constantProps = {
        appendContent: true,
        maybeInsertAd: true,
        isLoadMore: true,
        section,
    };
    return (fetchProps) =>
        fetch_content_graphql({ ...constantProps, ...fetchProps });
}

/**
 * Fetches Content Package for Preview from GraphQL and dispatches data into Content store slice.
 * @param {string} section the value that is dispatched into content.section
 * @param {string} previewId the polopoly id of content being previewed
 * @param {string} preview type of preview: "nine", "polopoly", "" (none)
 * @return {Function} dispatch receive_content
 */
export function fetch_content_preview({
    section,
    previewId,
    preview = "nine",
}) {
    return async (dispatch) => {
        if (!section || !previewId) {
            return;
        }

        try {
            let transformedItems;

            const response = await fetchContentPackage(previewId, preview);
            transformedItems = transformAllContentItems([response.contentItem]);

            const firstItem = transformedItems[0];
            const contentPackage = firstItem?.packageComponents
                ? firstItem
                : null;

            dispatch(
                receive_content({
                    contentList: transformedItems,
                    pageValue: 1,
                    section: section,
                    timestamp: Date.now(),
                    status: CONSTANTS.STATUS_CODES.SUCCESS,
                    contentPackage,
                })
            );
        } catch (error) {
            logger.error(
                `(fetch_content_preview) fetchContentPackage failed for ${previewId}`
            );
        }
    };
}
